import { useState, useEffect } from 'react';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { validateImageFile } from '../../lib/uploadValidation';

const COMPANY_TYPES = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'retailer', label: 'Retailer' },
  { value: 'agent', label: 'Agent' },
  { value: 'distributor', label: 'Distributor' },
];

const DEFAULT_CATEGORIES = [
  'Arts & Crafts', 'Action Figures', 'Fashion Dolls', 'Pre-School',
  'Electronics', 'Family Games', 'Party Games', 'Sports & Outdoor',
  'Feature Plush', 'Collectibles', 'STEM / Educational', 'Books',
];

export default function CompanyProfileForm() {
  const { user } = useAuth();
  const { profile, loading, saving, save } = useCompanyProfile();
  const [form, setForm] = useState({
    company_name: '', company_type: '', website: '', categories: [], logo_url: '',
  });
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        company_name: profile.company_name || '',
        company_type: profile.company_type || '',
        website: profile.website || '',
        categories: profile.categories || [],
        logo_url: profile.logo_url || '',
      });
    }
  }, [profile]);

  const update = (key, val) => { setForm(prev => ({ ...prev, [key]: val })); setSaved(false); };

  const toggleCategory = (cat) => {
    setForm(prev => ({
      ...prev,
      categories: prev.categories.includes(cat)
        ? prev.categories.filter(c => c !== cat)
        : [...prev.categories, cat],
    }));
    setSaved(false);
  };

  const uploadLogo = async (file) => {
    const err = validateImageFile(file);
    if (err) { alert(err); return; }
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `company-logos/${user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('event-assets').upload(path, file);
    if (error) { alert('Upload failed: ' + error.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('event-assets').getPublicUrl(path);
    update('logo_url', urlData.publicUrl);
    setUploading(false);
  };

  const handleSave = async () => {
    try {
      await save(form);
      setSaved(true);
    } catch (err) {
      alert('Save failed: ' + err.message);
    }
  };

  if (loading) return <div className="text-sm text-gray-400">Loading profile...</div>;

  const inputCls = "w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors";
  const labelCls = "block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5";

  return (
    <div className="glass p-7 max-w-2xl">
      <h2 className="font-heading text-xl text-vault-navy mb-5">Company Profile</h2>

      <div className="space-y-5">
        <div>
          <label className={labelCls}>Company Name *</label>
          <input className={inputCls} value={form.company_name} onChange={e => update('company_name', e.target.value)} />
        </div>

        <div>
          <label className={labelCls}>Company Type</label>
          <select className={inputCls} value={form.company_type} onChange={e => update('company_type', e.target.value)}>
            <option value="">Select type...</option>
            {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div>
          <label className={labelCls}>Website</label>
          <input className={inputCls} value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://..." />
        </div>

        <div>
          <label className={labelCls}>Categories of Interest</label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {DEFAULT_CATEGORIES.map(cat => (
              <label key={cat} className={`flex items-center gap-2 p-2.5 rounded-input border-2 cursor-pointer transition-colors text-sm ${
                form.categories.includes(cat) ? 'border-vault-gold bg-amber-50/50' : 'border-vault-border hover:border-gray-300'
              }`}>
                <input
                  type="checkbox"
                  checked={form.categories.includes(cat)}
                  onChange={() => toggleCategory(cat)}
                  className="w-4 h-4 accent-amber-500"
                />
                {cat}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>Company Logo</label>
          {form.logo_url && <img src={form.logo_url} alt="Logo" className="h-16 rounded mb-2" />}
          <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadLogo(e.target.files[0])} disabled={uploading} />
        </div>

        <div className="flex items-center gap-4 pt-4 border-t border-vault-border">
          <button
            onClick={handleSave}
            disabled={saving || !form.company_name}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn disabled:opacity-50 transition-all"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {saved && <span className="text-emerald-600 text-sm font-medium">Saved!</span>}
        </div>
      </div>
    </div>
  );
}